/**
 * Compatibilidade de template WhatsApp com relatório de inteligência operacional.
 * Padrão: aprovado, não AUTH, não MARKETING; header alinhado ao formato (texto/imagem/vídeo/documento).
 */

export const FORMAT_EXPECTED_HEADER = {
  text: "none",
  audio: "document",
  image_chart: "image",
  infographic: "image",
  video_narrated: "video",
  video_animated: "video",
};

export const HEADER_KIND_LABELS = {
  none: "None / Text (sem mídia)",
  text: "Text",
  image: "Image",
  video: "Video",
  document: "Document",
  location: "Location",
  unknown: "Desconhecido",
};

export function extractBodyText(rawData) {
  if (rawData == null) return "";
  const str = String(rawData).trim();
  if (!str) return "";
  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed === "object") {
      if (typeof parsed.body === "string") return parsed.body;
      if (Array.isArray(parsed.components)) {
        const bodyComponent = parsed.components.find(
          (c) => c && String(c.type || "").toUpperCase() === "BODY"
        );
        if (typeof bodyComponent?.text === "string") return bodyComponent.text;
      }
      if (typeof parsed.data === "string") return parsed.data;
    }
  } catch {
    /* texto puro */
  }
  return str;
}

export function countBodyPlaceholders(bodyText) {
  const matches = String(bodyText || "").match(/\{\{(\d+)\}\}/g);
  if (!Array.isArray(matches) || !matches.length) return 0;
  return Math.max(
    ...matches
      .map((m) => parseInt(m.replace(/\D/g, ""), 10))
      .filter((n) => Number.isFinite(n)),
    1
  );
}

function normalizeCategory(category) {
  return String(category || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

function normalizeStatus(status) {
  return String(status || "")
    .trim()
    .toLowerCase();
}

export function normalizeHeaderKind(raw) {
  const v = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (!v) return null;
  if (v === "none" || v === "null" || v === "empty") return "none";
  if (v === "text" || v === "texto") return "text";
  if (v === "image" || v === "imagem" || v === "img") return "image";
  if (v === "video" || v === "vídeo" || v === "video_mp4") return "video";
  if (v === "document" || v === "documento" || v === "file" || v === "doc" || v === "pdf") {
    return "document";
  }
  if (v === "location" || v === "localizacao" || v === "localização") return "location";
  return null;
}

function headerKindFromComponents(components) {
  if (!Array.isArray(components)) return null;
  const header = components.find((c) => c && String(c.type || "").toUpperCase() === "HEADER");
  if (!header) return "none";
  return normalizeHeaderKind(header.format || header.header_type || header.type) || "none";
}

function resolveBodyText(template) {
  return (
    template?.bodyText ||
    template?.preview ||
    extractBodyText(template?.data) ||
    ""
  );
}

export function resolveWhatsappTemplateHeaderKind(template) {
  if (!template) return "unknown";
  if (template.headerKind) {
    const fromField = normalizeHeaderKind(template.headerKind);
    if (fromField) return fromField;
  }
  if (template.headerType) {
    const fromField = normalizeHeaderKind(template.headerType);
    if (fromField) return fromField;
  }
  const fromType = normalizeHeaderKind(template.templateType || template.type);
  if (fromType) return fromType;

  const data = template.data;
  if (data != null) {
    if (typeof data === "object" && Array.isArray(data.components)) {
      const fromComp = headerKindFromComponents(data.components);
      if (fromComp) return fromComp;
    }
    if (typeof data === "string" && data.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed?.components)) {
          const fromComp = headerKindFromComponents(parsed.components);
          if (fromComp) return fromComp;
        }
      } catch {
        /* ignore */
      }
    }
  }
  return "none";
}

export function expectedHeaderForFormat(formatKey) {
  return FORMAT_EXPECTED_HEADER[String(formatKey || "").trim()] || "none";
}

export function headersCompatible(expected, actual) {
  const exp = normalizeHeaderKind(expected) || "none";
  const act = normalizeHeaderKind(actual) || "none";
  // Texto: só None/Text. Não aceitar unknown (evita mídia mal classificada no seletor de texto).
  if (exp === "none" || exp === "text") {
    return act === "none" || act === "text";
  }
  return act === exp;
}

/**
 * @returns {{
 *   ok: boolean,
 *   code?: string,
 *   severity?: 'error'|'warning',
 *   bodyParamCount?: number,
 *   headerKind?: string,
 *   expectedHeader?: string,
 * }}
 */
export function assessOpsIntelligenceWhatsappTemplate(template, opts = {}) {
  if (!template || template.id == null) {
    return { ok: false, code: "required", severity: "error", bodyParamCount: 0 };
  }

  const status = normalizeStatus(template.status);
  const approved = ["approved", "active", "enabled"].includes(status);
  if (!approved) {
    return {
      ok: false,
      code: "not_approved",
      severity: "error",
      bodyParamCount: Number(template.bodyParamCount) || 0,
    };
  }

  const category = normalizeCategory(template.category || "");
  if (category.includes("AUTH")) {
    return {
      ok: false,
      code: "authentication",
      severity: "error",
      bodyParamCount: Number(template.bodyParamCount) || 0,
    };
  }
  if (category.includes("MARKETING")) {
    return {
      ok: false,
      code: "marketing",
      severity: "error",
      bodyParamCount: Number(template.bodyParamCount) || 0,
    };
  }

  let bodyParamCount = Number(template.bodyParamCount);
  if (!Number.isFinite(bodyParamCount)) {
    bodyParamCount = countBodyPlaceholders(resolveBodyText(template));
  }

  const headerKind = resolveWhatsappTemplateHeaderKind(template);
  const expectedHeader = opts.expectedHeader != null ? opts.expectedHeader : "none";

  if (!headersCompatible(expectedHeader, headerKind)) {
    return {
      ok: false,
      code: "wrong_header",
      severity: "error",
      bodyParamCount,
      headerKind,
      expectedHeader: normalizeHeaderKind(expectedHeader) || "none",
    };
  }

  const exp = normalizeHeaderKind(expectedHeader) || "none";
  const needsBodyVar = exp === "none" || exp === "text";
  if (needsBodyVar && bodyParamCount <= 0) {
    return {
      ok: false,
      code: "missing_summary_variable",
      severity: "error",
      bodyParamCount: 0,
      headerKind,
    };
  }

  return { ok: true, bodyParamCount, headerKind };
}

export function isOpsIntelligenceWhatsappTemplateSelectable(template, expectedHeader = "none") {
  return assessOpsIntelligenceWhatsappTemplate(template, { expectedHeader }).ok === true;
}

/**
 * Briefing de exemplo no mesmo formato do envio real (`buildWhatsappManagerBriefing` na API):
 * uma linha com " | " (Meta #132018), sem URLs S3 — mídias vão em mensagens/header separados.
 */
export function buildOpsIntelligenceSampleWhatsappBriefing({
  formats = ["text"],
  locale = "pt",
} = {}) {
  const formatSet = new Set((formats || []).map(String));
  const pt = locale !== "en" && locale !== "es";
  const hasMedia = [...formatSet].some(
    (f) => f !== "text" && (f === "audio" || f === "infographic" || f === "image_chart" || String(f).startsWith("video"))
  );

  const parts = pt
    ? [
        "*Ops 01/08/2026 → 06/08/2026*",
        "Vol 1.240 (+12% vs período anterior)",
        "Abertos 186 (15%) · Concl 980 (79%)",
        "Espera 4min · Atend 12min",
        "Canal #1 WhatsApp (820)",
        "Equipe 14 · pico Maria (98)",
        "A fila aberta preocupa mais que o volume: redistribua casos >24h no WhatsApp.",
        "*Atenção* Concentração no WhatsApp; tipo Reclamação em alta",
        "*Faça* 1) Redistribuir fila >24h → Supervisor 2) Reforçar plantão no pico → Coordenação",
      ]
    : locale === "es"
      ? [
          "*Ops 01/08/2026 → 06/08/2026*",
          "Vol 1.240 (+12% vs período anterior)",
          "Abiertos 186 (15%) · Cerrados 980 (79%)",
          "Espera 4min · Atención 12min",
          "Canal #1 WhatsApp (820)",
          "Equipo 14 · pico María (98)",
          "La cola abierta preocupa más que el volumen: redistribuya casos >24h en WhatsApp.",
          "*Atención* Concentración en WhatsApp; reclamos en alza",
          "*Haga* 1) Redistribuir cola >24h → Supervisor 2) Reforzar turno pico → Coordinación",
        ]
      : [
          "*Ops 01/08/2026 → 06/08/2026*",
          "Vol 1,240 (+12% vs prior period)",
          "Open 186 (15%) · Done 980 (79%)",
          "Wait 4min · Handle 12min",
          "#1 channel WhatsApp (820)",
          "Team 14 · peak Maria (98)",
          "Open queue matters more than volume: redistribute >24h WhatsApp cases.",
          "*Watch* WhatsApp concentration; complaints rising",
          "*Do* 1) Redistribute >24h queue → Supervisor 2) Reinforce peak shift → Coordination",
        ];

  if (hasMedia) {
    parts.push(
      pt
        ? "Mídias nas msgs seguintes. Para reenviar diga Infográfico, Áudio ou Vídeo."
        : locale === "es"
          ? "Medios en los msgs siguientes. Para reenviar diga Infografía, Audio o Video."
          : "Media in the following messages. To resend say Infographic, Audio or Video."
    );
  } else {
    parts.push(
      pt
        ? "Detalhes e perguntas no chat XPro."
        : locale === "es"
          ? "Detalles y preguntas en el chat XPro."
          : "Details and questions in the XPro chat."
    );
  }

  let out = parts
    .map((p) => String(p || "").replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join(" | ");
  if (out.length > 900) out = `${out.slice(0, 897)}…`;
  return out;
}

/** Espelho do Header de mídia enviado no template (Meta/Gupshup). */
export function headerMediaPreviewForFormat(formatKey) {
  const key = String(formatKey || "").trim();
  const expected = expectedHeaderForFormat(key);
  if (expected === "none" || expected === "text") return null;
  const map = {
    audio: {
      type: "document",
      filename: "briefing-audio.mp3",
      mimeHint: "audio/mpeg",
      label: "Áudio MP3 no Header Document",
    },
    image_chart: {
      type: "image",
      filename: "grafico.png",
      mimeHint: "image/png",
      label: "Gráfico no Header Image",
    },
    infographic: {
      type: "image",
      filename: "infografico.png",
      mimeHint: "image/png",
      label: "Infográfico no Header Image",
    },
    video_narrated: {
      type: "video",
      filename: "video_narrated.mp4",
      mimeHint: "video/mp4",
      label: "Vídeo explicativo no Header Video",
    },
    video_animated: {
      type: "video",
      filename: "video_animated.mp4",
      mimeHint: "video/mp4",
      label: "Vídeo animado no Header Video",
    },
  };
  return (
    map[key] || {
      type: expected,
      filename: "midia",
      mimeHint: "",
      label: `Header ${expected}`,
    }
  );
}

/**
 * Meta (#132018): valores de {{n}} no template não podem ter \\n/\\t nem >4 espaços seguidos.
 */
export function sanitizeWhatsappTemplateParamText(raw, maxLen = 900) {
  let s = String(raw || "")
    .replace(/\r\n/g, "\n")
    .replace(/\t/g, " ")
    .replace(/\n+/g, " | ")
    .replace(/\s{2,}/g, " ")
    .replace(/(?:\s*[·|]\s*){2,}/g, " | ")
    .replace(/^[·|\s]+|[·|\s]+$/g, "")
    .trim();
  s = s.replace(/ {5,}/g, " ");
  const limit = Math.max(40, Math.min(1024, Number(maxLen) || 900));
  if (s.length > limit) s = `${s.slice(0, limit - 1)}…`;
  return s || "Resumo operacional disponível no chat.";
}

export function buildOpsIntelligenceSampleParams(bodyParamCount = 1, localeHints = {}) {
  const name = sanitizeWhatsappTemplateParamText(localeHints.name || "Ana Silva", 60);
  const summary = sanitizeWhatsappTemplateParamText(
    localeHints.summary ||
      buildOpsIntelligenceSampleWhatsappBriefing({
        formats: localeHints.formats || ["text"],
        locale: localeHints.locale || "pt",
      }),
    900
  );
  const needed = Math.max(0, Number(bodyParamCount) || 0);
  const params = [];
  if (needed === 1) {
    params.push(summary);
  } else if (needed > 1) {
    params.push(name);
    params.push(summary);
  }
  while (params.length < needed) params.push("-");
  return params.slice(0, needed);
}

export function fillWhatsappTemplateBody(bodyText, params = []) {
  const body = String(bodyText || "");
  if (!body) return "";
  return body.replace(/\{\{(\d+)\}\}/g, (_, num) => {
    const idx = Number(num) - 1;
    if (!Number.isFinite(idx) || idx < 0) return "";
    const value = params[idx];
    return value != null && String(value).trim() !== "" ? String(value) : "—";
  });
}

/**
 * Prévia fiel ao canal: body do template + briefing no formato real + header de mídia do formato.
 */
export function buildOpsIntelligenceDeliveryPreview(template, localeHints = {}, opts = {}) {
  const formatKey = opts.formatKey || "text";
  const formats = Array.isArray(opts.formats) && opts.formats.length ? opts.formats : [formatKey];
  const assessment = assessOpsIntelligenceWhatsappTemplate(template, opts);
  const body = resolveBodyText(template);
  const hints = {
    ...localeHints,
    formats,
    summary:
      localeHints.summary ||
      buildOpsIntelligenceSampleWhatsappBriefing({
        formats,
        locale: localeHints.locale || "pt",
      }),
  };
  const params = buildOpsIntelligenceSampleParams(assessment.bodyParamCount || 0, hints);
  const headerMedia = headerMediaPreviewForFormat(formatKey);
  return {
    ok: assessment.ok,
    assessment,
    bodyRaw: body,
    params,
    filledText: fillWhatsappTemplateBody(body, params),
    headerKind: assessment.headerKind || resolveWhatsappTemplateHeaderKind(template),
    expectedHeader: expectedHeaderForFormat(formatKey),
    headerMedia,
    formatKey,
    formats,
  };
}

export function opsTemplateIssueCopy(t, assessment) {
  if (!assessment?.code) return null;
  const code = assessment.code === "marketing_warning" ? "marketing" : assessment.code;
  const base = `agentsListPage.opsTemplateIssues.${code}`;
  const defaults = {
    wrong_header: {
      title: "Header incompatível",
      cause: `Este template tem Header ${assessment.headerKind || "?"}, mas o formato exige ${assessment.expectedHeader || "?"}.`,
      fix: "Na Meta/Gupshup, crie ou selecione um template Utilidade com o Header certo (Image, Video ou Document) e importe no canal.",
    },
  };
  const fallback = defaults[code] || { title: code, cause: "", fix: "" };
  return {
    title: t(base + ".title", { defaultValue: fallback.title }),
    cause: t(base + ".cause", { defaultValue: fallback.cause }),
    fix: t(base + ".fix", { defaultValue: fallback.fix }),
    severity: assessment.severity || "error",
  };
}

export const OPS_WA_SAMPLE_TEMPLATE_TEXT = `Olá {{1}},

Segue o briefing da operação no período:
{{2}}`;

export const OPS_WA_SAMPLE_BY_HEADER = {
  none: `Na Meta/Gupshup:
1. Categoria: Utility / Utilidade
2. Header: None (ou Text)
3. Body com variáveis, por exemplo:

Olá {{1}},

Segue o briefing da operação no período:
{{2}}`,
  image: `Na Meta/Gupshup:
1. Categoria: Utility / Utilidade
2. Header: Image  ← obrigatório para Gráfico e Infográfico
3. Body (opcional com variáveis):

Olá {{1}},
Segue o gráfico/infográfico do período:
{{2}}

O XPro envia a imagem gerada no Header deste template.`,
  video: `Na Meta/Gupshup:
1. Categoria: Utility / Utilidade
2. Header: Video  ← obrigatório para Vídeo explicativo e Vídeo animado
3. Body (opcional com variáveis):

Olá {{1}},
Segue o vídeo do briefing operacional:
{{2}}

O XPro envia o MP4 gerado no Header deste template.`,
  document: `Na Meta/Gupshup:
1. Categoria: Utility / Utilidade
2. Header: Document  ← obrigatório para Áudio (não existe Header Áudio)
3. Body (opcional com variáveis):

Olá {{1}},
Segue o áudio do briefing (arquivo):
{{2}}

O XPro envia o áudio em MP3 (audio/mpeg) no Header Document deste template.`,
};
OPS_WA_SAMPLE_BY_HEADER.text = OPS_WA_SAMPLE_BY_HEADER.none;

/** @deprecated use OPS_WA_SAMPLE_TEMPLATE_TEXT */
export const OPS_WA_SAMPLE_TEMPLATE = OPS_WA_SAMPLE_TEMPLATE_TEXT;

export function sampleTemplateCopyForFormat(formatKey) {
  const expected = expectedHeaderForFormat(formatKey);
  return OPS_WA_SAMPLE_BY_HEADER[expected] || OPS_WA_SAMPLE_BY_HEADER.none;
}
